import React, { useState } from 'react'
import { Typography, Divider } from '@material-ui/core'
import WarningRoundedIcon from '@material-ui/icons/WarningRounded'
import { FormattedMessage } from 'react-intl'
import * as R from 'ramda'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import {
  isCanceled,
  isEnded,
} from 'app/main/messages/notificationTimeline/unsendNotification/unsendNotificationLogic'
import { EventActionType } from 'app/main/messages/notificationTimeline/useEventActionsSubscribers'
import { notificationEventStatus } from 'app/main/notification/useNotificationEventStatus'
import effects from 'app/effects/effects'

const useStyles = makeStyles((theme) => ({
  eventActions: {
    backgroundColor: theme.palette.event.eventBackground,
  },
  eventDivider: {
    backgroundColor: theme.palette.event.eventBorder,
  },
  backgroundNormal: {
    backgroundColor: theme.palette.event.eventBackground,
  },
  backgroundUpcoming: {
    backgroundColor: theme.palette.event.upcomingBackground,
  },
  backgroundHappening: {
    backgroundColor: theme.palette.event.happeningBackground,
  },
  backgroundEnded: {
    backgroundColor: theme.palette.event.endedBackground,
  },
  textNormal: {
    color: theme.palette.event.textNormal,
  },
  textUpcoming: {
    color: theme.palette.warning.main,
  },
  textHappening: {
    color: theme.palette.active.main,
  },
  textEnded: {
    color: theme.palette.map.main,
  },
  textCanceled: {
    color: theme.palette.event.eventCanceled,
  },
  borderNormal: {
    borderColor: `${theme.palette.event.eventBorder} !important`,
  },
  borderUpcoming: {
    borderColor: `${theme.palette.event.upcomingBorder} !important`,
  },
  borderHappening: {
    borderColor: `${theme.palette.event.happeningBorder} !important`,
  },
  borderEnded: {
    borderColor: `${theme.palette.event.endedBorder} !important`,
  },
  borderCanceled: {
    borderColor: `${theme.palette.event.canceledBorder} !important`,
  },
  canceledRoot: {
    backgroundColor: theme.palette.event.eventCanceledBackground,
  },
  canceled: {
    color: theme.palette.event.eventCanceled,
  },
  reasonText: {
    fontSize: '1.4rem',
  },
}))

const actionViewClass = 'flex px-24 min-h-24'

const NotificationCardEventAction = ({
  notification,
  openEventActionSubscribersDialog,
}) => {
  const isCanceledValue = isCanceled(notification)

  const isEndedValue = isEnded(notification)

  const status = R.prop('eventStatus')(notification)

  const classes = useStyles()

  if (isCanceledValue) {
    return (
      <CanceledNotificationSection
        notification={notification}
        classes={classes}
        openEventActionSubscribersDialog={openEventActionSubscribersDialog}
      />
    )
  }
  if (isEndedValue) {
    return (
      <div className={clsx(classes.backgroundEnded, 'mx-16')}>
        <Divider className={classes.backgroundEnded} />
        <div className="py-8">
          <EndedNotificationSection
            notification={notification}
            classes={classes}
            openEventActionSubscribersDialog={openEventActionSubscribersDialog}
          />
        </div>
        <Divider className={classes.backgroundEnded} />
      </div>
    )
  }
  return (
    <div
      className={clsx(
        {
          [classes.backgroundUpcoming]:
            status === notificationEventStatus.upcoming.status,
          [classes.backgroundHappening]:
            status === notificationEventStatus.happening.status,
          [classes.backgroundNormal]:
            status === notificationEventStatus.normal.status,
        },
        'mx-16',
      )}
    >
      <Divider
        className={clsx({
          [classes.backgroundUpcoming]:
            status === notificationEventStatus.upcoming.status,
          [classes.backgroundHappening]:
            status === notificationEventStatus.happening.status,
          [classes.backgroundNormal]:
            status === notificationEventStatus.normal.status,
        })}
      />
      <div className="py-8">
        <EventCountSection
          classes={classes}
          notification={notification}
          openEventActionSubscribersDialog={openEventActionSubscribersDialog}
        />
      </div>
      <Divider
        className={clsx({
          [classes.backgroundUpcoming]:
            status === notificationEventStatus.upcoming.status,
          [classes.backgroundHappening]:
            status === notificationEventStatus.happening.status,
          [classes.backgroundNormal]:
            status === notificationEventStatus.normal.status,
        })}
      />
    </div>
  )
}

const EventCountSection = ({
  notification,
  classes,
  openEventActionSubscribersDialog,
}) => {
  let [notificationState, setNotificationState] = useState(notification)
  const status = R.prop('eventStatus')(notification)
  let { going, maybe, notGoing } = R.propOr(
    { going: 0, maybe: 0, notGoing: 0 },
    'eventActionsCount',
    notification,
  )

  const updateSubscribersCount = (notificationId, actionType, action) => {
    openEventActionSubscribersDialog(notificationId, actionType, action)
    effects.apis
      .getNotiEventSubscribers({
        notificationId: notificationId,
        action: actionType,
      })
      .then((res) => {
        let count = res.data.length
        if (actionType === EventActionType.GOING) {
          notification.eventActionsCount = {
            going: count,
            notGoing: notGoing,
            maybe: maybe,
          }
        } else if (actionType === EventActionType.NOT_GOING) {
          notification.eventActionsCount = {
            going: going,
            notGoing: count,
            maybe: maybe,
          }
        } else if (actionType === EventActionType.MAYBE) {
          notification.eventActionsCount = {
            going: going,
            notGoing: notGoing,
            maybe: count,
          }
        }
        setNotificationState(notification)
      })
  }

  const actionText = (action) => (
    <Typography
      className={clsx('text-center uppercase flex self-center font-semibold', {
        [classes.textUpcoming]:
          status === notificationEventStatus.upcoming.status,
        [classes.textHappening]:
          status === notificationEventStatus.happening.status,
        [classes.textEnded]: status === notificationEventStatus.ended.status,
        [classes.textCanceled]:
          status === notificationEventStatus.canceled.status,
        [classes.textNormal]: status === notificationEventStatus.normal.status,
      })}
      color="secondary"
    >
      {action}
    </Typography>
  )
  return (
    <div className="flex justify-center">
      <div
        onClick={() => {
          updateSubscribersCount(
            notificationState.id,
            EventActionType.GOING,
            going,
          )
        }}
        className={clsx(
          {
            [classes.borderUpcoming]:
              status === notificationEventStatus.upcoming.status,
            [classes.borderHappening]:
              status === notificationEventStatus.happening.status,
            [classes.borderEnded]:
              status === notificationEventStatus.ended.status,
            [classes.borderCanceled]:
              status === notificationEventStatus.canceled.status,
            [classes.borderNormal]:
              status === notificationEventStatus.normal.status,
          },
          actionViewClass,
          'border-r-1',
          going > 0 ? 'cursor-pointer' : 'cursor-default',
        )}
      >
        {actionText(
          <FormattedMessage
            id="notificationTimeline.card.event.action.going"
            values={{ count: notificationState.eventActionsCount.going }}
          />,
        )}
      </div>
      <div
        onClick={() => {
          updateSubscribersCount(
            notificationState.id,
            EventActionType.MAYBE,
            maybe,
          )
        }}
        className={clsx(
          {
            [classes.borderUpcoming]:
              status === notificationEventStatus.upcoming.status,
            [classes.borderHappening]:
              status === notificationEventStatus.happening.status,
            [classes.borderEnded]:
              status === notificationEventStatus.ended.status,
            [classes.borderCanceled]:
              status === notificationEventStatus.canceled.status,
            [classes.borderNormal]:
              status === notificationEventStatus.normal.status,
          },
          actionViewClass,
          'border-r-1',
          maybe > 0 ? 'cursor-pointer' : 'cursor-default',
        )}
      >
        {actionText(
          <FormattedMessage
            id="notificationTimeline.card.event.action.maybe"
            values={{ count: notificationState.eventActionsCount.maybe }}
          />,
        )}
      </div>
      <div
        onClick={() => {
          updateSubscribersCount(
            notificationState.id,
            EventActionType.NOT_GOING,
            notGoing,
          )
        }}
        className={clsx(
          {
            [classes.borderUpcoming]:
              status === notificationEventStatus.upcoming.status,
            [classes.borderHappening]:
              status === notificationEventStatus.happening.status,
            [classes.borderEnded]:
              status === notificationEventStatus.ended.status,
            [classes.borderCanceled]:
              status === notificationEventStatus.canceled.status,
            [classes.borderNormal]:
              status === notificationEventStatus.normal.status,
          },
          actionViewClass,
          notGoing > 0 ? 'cursor-pointer' : 'cursor-default',
        )}
      >
        {actionText(
          <FormattedMessage
            id="notificationTimeline.card.event.action.notGoing"
            values={{ count: notificationState.eventActionsCount.notGoing }}
          />,
        )}
      </div>
    </div>
  )
}

const CanceledNotificationSection = ({
  notification,
  classes,
  openEventActionSubscribersDialog,
}) => (
  <div className={clsx('py-24', classes.canceledRoot)}>
    <div className="flex flex-col pb-24 justify-center items-center">
      <WarningRoundedIcon fontSize="large" className={classes.canceled} />
      <Typography className={clsx('text-base font-bold', classes.canceled)}>
        <FormattedMessage id="notificationTimeline.card.status.canceled.title" />
      </Typography>
      {R.compose(
        R.unless(R.isNil, (reason) => (
          <Typography className={clsx(classes.reasonText, classes.canceled)}>
            {reason}
          </Typography>
        )),
        R.propOr(null, 'cancelReason'),
      )(notification)}
    </div>
    <EventCountSection
      notification={notification}
      classes={classes}
      openEventActionSubscribersDialog={openEventActionSubscribersDialog}
    />
  </div>
)

const EndedNotificationSection = ({
  notification,
  classes,
  openEventActionSubscribersDialog,
}) => (
  <>
    <div className="flex flex-col justify-center items-center">
      <WarningRoundedIcon fontSize="large" className={classes.textEnded} />
      <Typography
        className={clsx('text-base font-bold pb-24', classes.textEnded)}
      >
        <FormattedMessage id="notificationTimeline.card.status.ended.title" />
      </Typography>
    </div>
    <EventCountSection
      notification={notification}
      classes={classes}
      openEventActionSubscribersDialog={openEventActionSubscribersDialog}
    />
  </>
)

export default NotificationCardEventAction
